import { useLazyQuery } from 'data-fetching-client';
import { useSelector } from 'react-redux';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import { GET_VALIDATION_ERRORS } from '../data/queries';
import { useCountryMetadata } from '../components/Address/FullAddress';
import { getBuyerOverridesLineItems, getBuyerOverridesPartialPaymentAmount } from '../state/BuyerOverrides/selectors';
import { useDebounce } from './useDebounce';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { getIsPaymentPurchaseUrl } from '../utils/urls';
import { useLocation } from 'quartz-routing/react-router';
import { getTaxCalculationId } from '../state/calculateTaxSlice';
import { APPLE_PAY_METHOD, GOOGLE_PAY_METHOD } from '../constants/PaymentElement';
export function useValidationErrors() {
  const {
    pathname
  } = useLocation();
  const isPaymentPurchaseUrl = getIsPaymentPurchaseUrl(pathname);
  const [validationState, setValidationState] = useState({
    formFieldValidationErrors: [],
    subtotalsValidationErrors: [],
    isValid: false
  });
  const {
    formState,
    checkoutSession: checkoutSessionMap
  } = usePaymentFormContext();
  const checkoutSession = checkoutSessionMap.toJS();
  const stripePaymentMethod = formState.get('stripePaymentMethod');
  const billingAddress = formState.get('billingAddress');
  const shippingAddress = formState.get('shippingAddress');
  const countryCode = formState.getIn(['billingAddress', 'country']);
  const phoneNumbers = formState.get('phoneNumbers');
  const buyerOverridesLineItems = useSelector(getBuyerOverridesLineItems);
  const collectShippingAddress = checkoutSession.shippableCountries && !!checkoutSession.shippableCountries.length;
  const collectFullBillingAddress = checkoutSession.collectFullBillingAddress;
  const taxCalculationId = useSelector(getTaxCalculationId);
  const partialPaymentAmount = useSelector(getBuyerOverridesPartialPaymentAmount);
  const {
    countryMetadata
  } = useCountryMetadata(countryCode);
  const shouldCheckPostalCode = countryMetadata ? countryMetadata.fields.some(field => field.type === 'POSTAL_CODE') : false;
  const variables = useMemo(() => ({
    email: formState.get('email'),
    paymentMethod: formState.get('paymentMethod'),
    checkoutBuyerOverrides: {
      lineItems: buyerOverridesLineItems,
      discountCode: formState.get('isDiscountCodeApplied') && formState.get('discountCode') !== '' ? formState.get('discountCode') : null,
      paymentAmount: partialPaymentAmount
    },
    billingInfo: billingAddress && {
      paymentsAddress: Object.assign({
        line1: billingAddress.get('address'),
        line2: billingAddress.get('address2')
      }, billingAddress.toJS()),
      fullName: billingAddress.get('fullName') || formState.getIn(['bankInfo', 'accountHolderName']),
      shouldCheckPostalCode,
      shouldCheckBillingName: collectFullBillingAddress || stripePaymentMethod !== APPLE_PAY_METHOD && stripePaymentMethod !== GOOGLE_PAY_METHOD
    },
    shippingInfo: collectShippingAddress && shippingAddress ? {
      paymentsAddress: Object.assign({
        line1: shippingAddress.get('address'),
        line2: shippingAddress.get('address2')
      }, shippingAddress.toJS()),
      firstName: shippingAddress.get('firstName'),
      lastName: shippingAddress.get('lastName'),
      shouldCheckPostalCode
    } : undefined,
    phoneNumbers: phoneNumbers && phoneNumbers.toJS(),
    taxCalculationId
  }), [billingAddress, shippingAddress, phoneNumbers, buyerOverridesLineItems, collectFullBillingAddress, collectShippingAddress, shouldCheckPostalCode, formState, taxCalculationId, stripePaymentMethod, partialPaymentAmount]);
  const [getValidationErrors, {
    data,
    loading
  }] = useLazyQuery(GET_VALIDATION_ERRORS);
  const debouncedGetValidationErrors = useDebounce(getValidationErrors, 200);
  const flushDebouncedGetValidationErrors = useCallback(() => {
    debouncedGetValidationErrors.flush();
  }, [debouncedGetValidationErrors]);
  useEffect(() => {
    if (!formState.get('loading') && isPaymentPurchaseUrl) {
      debouncedGetValidationErrors({
        variables
      });
    }
  }, [debouncedGetValidationErrors, formState, variables, isPaymentPurchaseUrl]);
  useEffect(() => {
    if (data !== null && data !== void 0 && data.validationErrors) {
      var _data$validationError, _data$validationError2;
      setValidationState({
        formFieldValidationErrors: ((_data$validationError = data.validationErrors.formFieldValidationErrors) === null || _data$validationError === void 0 || (_data$validationError = _data$validationError[0]) === null || _data$validationError === void 0 ? void 0 : _data$validationError.errors) || [],
        subtotalsValidationErrors: ((_data$validationError2 = data.validationErrors.subtotalsValidationErrors) === null || _data$validationError2 === void 0 || (_data$validationError2 = _data$validationError2[0]) === null || _data$validationError2 === void 0 ? void 0 : _data$validationError2.errors) || [],
        isValid: data.validationErrors.isValid
      });
    }
  }, [data]);
  return {
    formFieldValidationErrors: validationState.formFieldValidationErrors,
    subtotalsValidationErrors: validationState.subtotalsValidationErrors,
    // if not a valid checkout url (i.e. iframe preview), we skip BE validation
    isValid: isPaymentPurchaseUrl ? validationState.isValid : true,
    loading,
    flushDebouncedGetValidationErrors
  };
}